import React from "react";
import classNames from "classnames";
import {
  useEditProjectWeatherReport,
  useRetrieveCurrentPlanzone,
} from "@planzone/view-models";
import { WeatherReports } from "@planzone/dto";
import { UseCases } from "@planzone/core";
import { Safeguard } from "@scenes/safeguard";
import { VisibilityWatcher } from "@common/visibilityWatcher";
import { Loader } from "@common/loader";
import { Props } from "./types";
import { SunnyIcon } from "./sunnyIcon";
import { PartlyCloudyIcon } from "./partlyCloudyIcon";
import { CloudyIcon } from "./cloudyIcon";
import { RainyIcon } from "./rainyIcon";
import { StormyIcon } from "./stormyIcon";
import { ToggleArrow } from "./toggleArrow";
import styles from "./styles.scss";

function WeatherReport({ weatherReport, project }: Props) {
  const { planzone } = useRetrieveCurrentPlanzone();
  const weatherReports =
    planzone.uuid === "d980bc6f-06d6-470d-bae7-0c587d774888"
      ? [
          {
            value: WeatherReports.SUNNY,
            Icon: SunnyIcon,
            color: "#ffcf4e",
          },
          {
            value: WeatherReports.CLOUDY,
            Icon: CloudyIcon,
            color: "#d5d5d5",
          },
          {
            value: WeatherReports.STORMY,
            Icon: StormyIcon,
            color: "#707070",
          },
        ]
      : [
          {
            value: WeatherReports.SUNNY,
            Icon: SunnyIcon,
            color: "#ffcf4e",
          },
          {
            value: WeatherReports.PARTLY_CLOUDY,
            Icon: PartlyCloudyIcon,
            color: "#cbddf7",
          },
          {
            value: WeatherReports.CLOUDY,
            Icon: CloudyIcon,
            color: "#d5d5d5",
          },
          {
            value: WeatherReports.RAINY,
            Icon: RainyIcon,
            color: "#707070",
          },
          {
            value: WeatherReports.STORMY,
            Icon: StormyIcon,
            color: "#707070",
          },
        ];
  const { color, Icon } =
    weatherReports.find(({ value }) => weatherReport === value) ||
    weatherReports[0];
  const { isRequestPending, editProjectWeatherReport } =
    useEditProjectWeatherReport(project);

  return (
    <Safeguard useCases={[UseCases.editProject]} context={{ project }}>
      {({ canEditProject }) => (
        <VisibilityWatcher
          className={classNames(styles.weatherReport, {
            [styles.readOnly]: !canEditProject,
          })}
        >
          {(isVisible, show, hide) => (
            <div
              className={classNames(styles.weather, {
                [styles.opened]: isVisible,
              })}
              onClick={(e) => {
                e.stopPropagation();
                canEditProject && (isVisible ? hide() : show());
              }}
            >
              <div
                className={styles.toggleButton}
                style={{ backgroundColor: color }}
                onClick={(e) => {
                  e.stopPropagation();
                  isVisible ? hide() : show();
                }}
              >
                <ToggleArrow className={styles.toggleIcon} />
              </div>
              {isVisible ? (
                [...weatherReports].reverse().map(({ Icon, value }) => (
                  <div
                    key={value}
                    className={styles.main}
                    onClick={() => {
                      if (!isRequestPending) {
                        editProjectWeatherReport({
                          project,
                          weatherReport: value,
                        });
                        hide();
                      }
                    }}
                  >
                    <Icon className={styles.weatherIcon} />
                  </div>
                ))
              ) : (
                <div className={styles.main}>
                  {isRequestPending ? (
                    <div className={styles.loader}>
                      <div className={styles.loaderMain}>
                        <Loader />
                      </div>
                    </div>
                  ) : (
                    <Icon className={styles.weatherIcon} />
                  )}
                </div>
              )}
            </div>
          )}
        </VisibilityWatcher>
      )}
    </Safeguard>
  );
}

export { WeatherReport };
